import { Injectable } from '@angular/core';
import {ShiyoulotHibetsuFile} from '../../shared/interfaces/shiyoulot-hibetsu-file';
import {
  COLLECTION_NAME,
  GENZAI_WASUU,
  GENZAI_WASUU_KANA,
  HEISHI_TOUTA_PROPERTIES,
  SHUKKA_WASUU,
  SHUKKA_WASUU_KANA,
  TEISEI_WASUU,
  TEISEI_WASUU_KANA,
} from '../../shared/constants';
import {CommonService} from './common.service';
import {HeishiToutaProperties, HeishiToutaPropertiesKana, yyyyMMdd} from '../../shared/typeAliases';
import {CsvService} from './csv.service';
import {AngularFirestore} from "@angular/fire/firestore";
import {SessionService} from "./session.service";
import firebase from "firebase";
import firestore = firebase.firestore;
import {firstValueFrom} from 'rxjs';

// constants (本ファイルでのみ使用)
const ID = 'id';
const NICHIREI = 'nichirei';
const NICHIREI_KANA = '日齢';
const HIDUKE_KANA = '日付';
const GENZAIWASUUKAKUNIN = 'genzaiWasuuKakunin';

// 現在羽数確認プロパティ
interface genzaiWasuuKakuninProperties {
  prop: typeof ID| HeishiToutaProperties | typeof SHUKKA_WASUU | typeof TEISEI_WASUU | typeof GENZAI_WASUU | typeof NICHIREI,
  name: typeof HIDUKE_KANA | HeishiToutaPropertiesKana | typeof SHUKKA_WASUU_KANA | typeof TEISEI_WASUU_KANA | typeof GENZAI_WASUU_KANA | typeof NICHIREI_KANA,
}

// 斃死淘汰プロパティ ( heishiToutaProperties )
interface geninProperties extends genzaiWasuuKakuninProperties {
  prop: HeishiToutaProperties,
  name: HeishiToutaPropertiesKana
}

// 飼養ロットの現在羽数確認データ
interface genzaiWasuuKakuninRowData {
  id: string,
  osuWasuu: number | '',
  ashiWasuu: number | '',
  kuchiWasuu: number | '',
  kikeiWasuu: number | '',
  shouWasuu: number | '',
  otherWasuu: number | '',
  heishiWasuu: number | '',
  toutaWasuu: number | '',
  shukkaWasuu: number | '',
  teiseiWasuu: number | '',
  genzaiWasuu: number | '',
  nichirei: number | ''
}


@Injectable({
  providedIn: 'root'
})
export class ShiyoulotHibetsuFileService {
  constructor(private commonService: CommonService,
              public csvService: CsvService,
              private db: AngularFirestore,
              private sessionService: SessionService,) { }
  /**
   * ルートコレクションの参照を返す
   * @param collectionName
   */
  genCustomerRootCollectionRef(collectionName: string) : firebase.firestore.CollectionReference<firebase.firestore.DocumentData> {
    const customerId = this.sessionService.session.personalSetting.customerId;
    return this.db.firestore.collection(COLLECTION_NAME.CUSTOMERS).doc(customerId).collection(collectionName);
  }

  /**
   * 飼養ロット日別ファイルの参照を返す
   * @param keishaId
   * @param id
   */
  genShiyoulotHibetsuFileRef(shiyoulotId: string, id: string) {
    return this.genCustomerRootCollectionRef(COLLECTION_NAME.SHIYOULOT)
      .doc(shiyoulotId).collection(COLLECTION_NAME.HIBETSUFILE).doc(id);
  }

  /**
   * 飼養ロット日別ファイル
   * 初期値セットした「ShiyoulotHibetsuFile」型のobjectを返す
   *
   * @param shiyoulotNumber
   * @param shiyoulotId
   * @param keishaId
   * @param today
   */
  getInitialShiyolotHibetsuFile(shiyoulotId: string, keishaId: string,
    today = this.commonService.getDateWithString()): ShiyoulotHibetsuFile {
    return getInitialShiyolotHibetsuFile(shiyoulotId, keishaId, today);
  }

  /**
   * CSV出力
   * TODO shiyoulot: any -> Shiyoulot
   * @param tableName
   * @param shiyoulotHibetsuFiles
   * @param shiyoulot
   */
  exportCSVForShiyoulot(tableName: typeof GENZAIWASUUKAKUNIN, shiyoulotHibetsuFiles: ShiyoulotHibetsuFile[], shiyoulot: any): void {
    // 淘汰羽数→osuWasuu、ashiWasuuなど項目ごとに分けて出力に変更。
    // →完了。HEISHI_TOUTA_PROPERTIESを元にgeninPropertiesとして反映。
    const geninProperties = Object.keys(HEISHI_TOUTA_PROPERTIES)
      .reduce((prev: geninProperties[], currentKey: string | HeishiToutaProperties) => {
        prev.push({prop: currentKey as HeishiToutaProperties, name: HEISHI_TOUTA_PROPERTIES[currentKey as HeishiToutaProperties]});
        return prev;
      }, []);
    const genzaiWasuuKakuninProperties: genzaiWasuuKakuninProperties[] = [
      {prop: ID, name: HIDUKE_KANA},
      ...geninProperties,
      {prop: SHUKKA_WASUU, name: SHUKKA_WASUU_KANA},
      {prop: TEISEI_WASUU, name: TEISEI_WASUU_KANA},
      {prop: GENZAI_WASUU, name: GENZAI_WASUU_KANA},
      {prop: NICHIREI, name: NICHIREI_KANA},
    ];
    console.log('genzaiWasuuKakuninProperties', genzaiWasuuKakuninProperties);

    const genzaiWasuuKakuninArr = shiyoulotHibetsuFiles.reduce((prev: genzaiWasuuKakuninRowData[], currentHibetsu: ShiyoulotHibetsuFile) => {
      const rowData = this.getInitialGenzaiWasuuKakuninRowData();
      rowData.id = this.commonService.emptyWhenFalse(currentHibetsu?.id);
      rowData.heishiWasuu = currentHibetsu.heishiWasuu ? currentHibetsu.heishiWasuu : '';
      rowData.toutaWasuu = typeof this.commonService.calcTouta(currentHibetsu) === 'number' ? this.commonService.calcTouta(currentHibetsu) : '';
      rowData.shukkaWasuu = this.commonService.emptyWhenFalse(currentHibetsu?.shukkaWasuu);
      rowData.teiseiWasuu = this.commonService.emptyWhenFalse(currentHibetsu?.teiseiWasuu);
      rowData.genzaiWasuu = typeof this.commonService.calcGenzaiWasuuForShiyoulotHibetsuFile(currentHibetsu) === 'number'
        ? this.commonService.calcGenzaiWasuuForShiyoulotHibetsuFile(currentHibetsu) : '';
      rowData.nichirei = typeof this.commonService.calcNichirei(this.commonService.getEdukeDate(shiyoulot), currentHibetsu.id) === 'number'
        ? this.commonService.calcNichirei(this.commonService.getEdukeDate(shiyoulot), currentHibetsu.id) : '';

      this.commonService.keysOf(HEISHI_TOUTA_PROPERTIES).forEach((property) => {
        if (rowData.hasOwnProperty(property) && currentHibetsu.hasOwnProperty(property)) {
          rowData[property] = this.commonService.emptyWhenFalse(currentHibetsu[property]);
        }
      });
      prev.push(rowData);
      return prev;
    }, []);

    console.log('genzaiWasuuKakuninArr', genzaiWasuuKakuninArr);
    const shiyoulotId = shiyoulot.shiyoulotId ?? shiyoulot.shiyoulotNumber;

    switch (tableName) {
      case GENZAIWASUUKAKUNIN:
        this.csvService.exportCsv(
          genzaiWasuuKakuninArr,
          genzaiWasuuKakuninProperties,
          'horizontal',
          '【飼養ロット番号' + shiyoulotId + '】現在羽数確認'
        );
        break;
    }
  }

  /**
   * 各飼養ロット毎の現在羽数確認データの初期値を返す
   */
  getInitialGenzaiWasuuKakuninRowData(): genzaiWasuuKakuninRowData {
    return {
      id: '',
      osuWasuu: '',
      ashiWasuu: '',
      kuchiWasuu: '',
      kikeiWasuu: '',
      shouWasuu: '',
      otherWasuu: '',
      heishiWasuu: '',
      toutaWasuu: '',
      shukkaWasuu: '',
      teiseiWasuu: '',
      genzaiWasuu: '',
      nichirei: ''
    };
  }

  /**
   * 飼養ロット日別ファイル取得クエリ生成
   * @param shiyoulotNumber
   * @param fromDate
   * @param toDate
   */
  genShiyoulotHibetsuFileQuery(shiyoulotNumber: string, fromDate: string, toDate: string) {
    const customerId = this.sessionService.session.personalSetting.customerId;
    return this.db.collection(COLLECTION_NAME.CUSTOMERS).doc(customerId)
      .collection(COLLECTION_NAME.SHIYOULOT).doc(shiyoulotNumber)
      .collection(COLLECTION_NAME.HIBETSUFILE, ref => {
        let query:
          | firestore.CollectionReference
          | firestore.Query = ref;
        query = query.where('id', '>=', fromDate);
        query = query.where('id', '<=', toDate);
        return query;
      });
  }

  /**
   * 飼養ロット日別ファイル取得
   * @param shiyoulotNumber
   * @param fromDate
   * @param toDate
   */
  getShiyoulotHibetsuFile(shiyoulotNumber: string, fromDate: string, toDate: string) {
    return new Promise<ShiyoulotHibetsuFile[]>(resolve => {
      const list: ShiyoulotHibetsuFile[] = [];
      return firstValueFrom(this.genShiyoulotHibetsuFileQuery(shiyoulotNumber, fromDate, toDate).get()).then(shiyoulotHibetsFiles => {
        shiyoulotHibetsFiles.docs.forEach(shiyoulotHibetsFile => {
          const shiyoulotHibetsFileMeisai = shiyoulotHibetsFile.data() as ShiyoulotHibetsuFile;
          shiyoulotHibetsFileMeisai.id = shiyoulotHibetsFile.id;
          list.push(shiyoulotHibetsFileMeisai);
        });
        return resolve(list);
      });
    });
  }

  /**
   * 飼養ロット日別ファイル生成
   * @param shiyoulotId
   * @param keishaId
   * @param fromDate
   * @param toDate
   */
  async genShiyoulotHibetsuFile(shiyoulotId: string, keishaId: string, fromDate: yyyyMMdd = '', toDate: yyyyMMdd = '') {
    // 飼養ロットの餌付け日〜予定出荷日までの飼養ロット日別ファイルを生成
    fromDate = fromDate ? fromDate : this.commonService.getDateWithString();
    toDate = toDate ? toDate : this.commonService.getDateWithString();
    console.log(fromDate, toDate);
    const dateArray = this.commonService.genDateStringInTerm(fromDate, toDate);
    const shiyoulotHibetsuFiles = await this.getShiyoulotHibetsuFile(shiyoulotId, fromDate, toDate) as ShiyoulotHibetsuFile[];
    const isDuplicated = this.commonService.isDuplicated(
      [...shiyoulotHibetsuFiles.map(shiyoulotHibetsuFile => shiyoulotHibetsuFile.id), ...dateArray]);
    if (isDuplicated) {
      if(!confirm('期間内に飼養ロット日別ファイルが存在していますが、初期値で上書きしますか？')) {
        return;
      }
    }
    const customerId = this.sessionService.session.personalSetting.customerId;
    const shiyoulotHibetsuFileRef = this.db.firestore.collection(COLLECTION_NAME.CUSTOMERS).doc(customerId)
      .collection(COLLECTION_NAME.SHIYOULOT).doc(shiyoulotId)
      .collection(COLLECTION_NAME.HIBETSUFILE);
    const batch = this.db.firestore.batch();
    dateArray.forEach(date => {
      batch.set(shiyoulotHibetsuFileRef.doc(date), this.getInitialShiyolotHibetsuFile(shiyoulotId, keishaId, date));
    });
    batch.commit().then();
  }
}

/**
 *
 * @param shiyoulotId
 * @param keishaId
 * @param date
 */
export function getInitialShiyolotHibetsuFile(shiyoulotId: string, keishaId: string,
  date: yyyyMMdd): ShiyoulotHibetsuFile {
  return {
    kind: COLLECTION_NAME.HIBETSUFILE,
    id: date,
    shiyoulotId,
    keishaId,
    genzaiWasuu: 0,
    nyuukaWasuu: 0,
    shukkaWasuu: 0,
    heishiWasuu: 0,
    ashiWasuu: 0,
    kikeiWasuu: 0,
    kuchiWasuu: 0,
    osuWasuu: 0,
    otherWasuu: 0,
    shouWasuu: 0,
    teiseiWasuu: 0,
  };
}
